import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * EditReferenceForm component - Form for editing reference metadata
 * (comment, rating, methods, focus, key findings, category)
 */
function EditReferenceForm({ reference, getData, categories }) {
  const [comment, setComment] = useState(reference.comment || '');
  const [rating, setRating] = useState(reference.rating || null);
  const [methods, setMethods] = useState(reference.methods || '');
  const [focus, setFocus] = useState(reference.focus || '');
  const [keyFindings, setKeyFindings] = useState(reference.key_findings || '');
  const [category, setCategory] = useState(reference.category || '');
  const [showNewCategory, setShowNewCategory] = useState(false);

  const commentRef = useRef(null);
  const methodsRef = useRef(null);
  const focusRef = useRef(null);
  const keyFindingsRef = useRef(null);

  // Auto-resize textarea based on content
  const autoResize = useCallback((textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, []);

  // Auto-resize all textareas on mount
  useEffect(() => {
    autoResize(commentRef.current);
    autoResize(methodsRef.current);
    autoResize(focusRef.current);
    autoResize(keyFindingsRef.current);
  }, [autoResize]);

  // Expose data through callback
  useEffect(() => {
    if (getData) {
      getData.current = () => ({
        comment,
        rating,
        methods,
        focus,
        key_findings: keyFindings,
        category: category || null
      });
    }
  }, [comment, rating, methods, focus, keyFindings, category, getData]);

  return (
    <div className="edit-form">
      <div className="form-group">
        <label>Category</label>
        <div className="category-input-group">
          {!showNewCategory ? (
            <>
              <select
                value={category}
                onChange={(e) => {
                  if (e.target.value === '__new__') {
                    setShowNewCategory(true);
                    setCategory('');
                  } else {
                    setCategory(e.target.value);
                  }
                }}
              >
                <option value="">No category</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
                <option value="__new__">+ New Category</option>
              </select>
            </>
          ) : (
            <>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Enter new category"
              />
              <button
                type="button"
                onClick={() => {
                  setShowNewCategory(false);
                  setCategory('');
                }}
                className="cancel-new-category"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      <div className="form-group">
        <label>Rating</label>
        <div className="rating-radio-group">
          {[1, 2, 3, 4, 5].map(star => (
            <label key={star} className="rating-radio-label">
              <input
                type="radio"
                name="rating"
                value={star}
                checked={rating === star}
                onChange={() => setRating(star)}
              />
              <span className="rating-star">{star}⭐</span>
            </label>
          ))}
          <label className="rating-radio-label">
            <input
              type="radio"
              name="rating"
              value=""
              checked={rating === null}
              onChange={() => setRating(null)}
            />
            <span className="rating-clear">Clear</span>
          </label>
        </div>
      </div>

      <div className="form-group">
        <label>Comment</label>
        <textarea
          ref={commentRef}
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
            autoResize(e.target);
          }}
          placeholder="Add a comment (supports markdown)"
          className="auto-resize-textarea"
          rows={1}
        />
      </div>

      <div className="form-group">
        <label>Methods</label>
        <textarea
          ref={methodsRef}
          value={methods}
          onChange={(e) => {
            setMethods(e.target.value);
            autoResize(e.target);
          }}
          placeholder="Research methods used (supports markdown)"
          className="auto-resize-textarea"
          rows={1}
        />
      </div>

      <div className="form-group">
        <label>Focus</label>
        <textarea
          ref={focusRef}
          value={focus}
          onChange={(e) => {
            setFocus(e.target.value);
            autoResize(e.target);
          }}
          placeholder="Main focus of the work (supports markdown)"
          className="auto-resize-textarea"
          rows={1}
        />
      </div>

      <div className="form-group">
        <label>Key Findings</label>
        <textarea
          ref={keyFindingsRef}
          value={keyFindings}
          onChange={(e) => {
            setKeyFindings(e.target.value);
            autoResize(e.target);
          }}
          placeholder="Key findings or conclusions (supports markdown)"
          className="auto-resize-textarea"
          rows={1}
        />
      </div>
    </div>
  );
}

export default EditReferenceForm;
